/**
 * Client-generated identity for records the server has not stored yet.
 *
 * When a save reaches the server but its reply is lost, the app is left
 * believing nothing was written and the next save re-sends the same records —
 * creating a second order, or inserting lines that already exist. The server
 * can only recognise that repeat if the records carry an id the client made
 * once and keeps across every retry; the database keys are no use, because the
 * server is the one that assigns them.
 *
 * Matches the API's `guid` fields, which are typed as uuid.
 */
export function newGuid(): string {
  const cryptoObj: Crypto | undefined = (globalThis as any).crypto;

  if (cryptoObj?.randomUUID) {
    return cryptoObj.randomUUID();
  }

  // Older Android WebViews carry getRandomValues without randomUUID.
  if (cryptoObj?.getRandomValues) {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
    return formatUuid(bytes);
  }

  // Last resort, so a missing crypto API can never leave a record without an
  // identity — that would put us straight back to duplicating on retry.
  const bytes = new Uint8Array(16);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = Math.floor(Math.random() * 256);
  }
  return formatUuid(bytes);
}

function formatUuid(bytes: Uint8Array): string {
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
}
